import { useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { PiPlus } from 'react-icons/pi'
import { PillTabs } from 'components/FbUI/PillTabs'
import { primaryPillButtonClass } from 'components/FbUI/secondaryPillButton'
import { cn } from 'components/UI/cn'
import { DashboardCard } from './components/DashboardCard'
import { LibraryRow, LibraryTable } from './components/LibraryTable'
import { SaveDialog } from './components/SaveDialog'
import { Dashboard, DASHBOARDS } from './dashboards'
import {
  deleteKpi,
  Kpi,
  listKpis,
  saveKpi,
  seedStarterKpi,
  updateKpi,
} from './kpis'
import { REPORTING_PAGE_SHELL } from './page-shell'
import {
  CURRENT_REP,
  loadPersona,
  Persona,
  PERSONAS,
  savePersona,
  visibleForPersona,
} from './persona'
import {
  addReportToDashboard,
  createDashboardFromReport,
  deleteSavedDashboard,
  listSavedDashboards,
  saveDashboard,
} from './saved-dashboards'
import { deleteSavedView, listSavedViews, saveView } from './saved-views'
import { TEMPLATES } from './templates'
import { ViewConfig } from './view-config'

/**
 * Reports library: shipped templates + the user's saved copies.
 * PROTOTYPE — mock data only, behind enable_reporting_prototype flag.
 */
export default function ReportingLibraryPage() {
  const [savedViews, setSavedViews] = useState(listSavedViews)
  const [savedDashboards, setSavedDashboards] = useState(listSavedDashboards)
  const [kpis, setKpis] = useState(() => {
    seedStarterKpi()
    return listKpis()
  })
  const [persona, setPersona] = useState<Persona>(loadPersona)
  // The report awaiting a name for a brand-new dashboard (null = dialog closed).
  const [newDashView, setNewDashView] = useState<ViewConfig | null>(null)

  const changePersona = (next: Persona) => {
    // Persist first so the per-persona storage keys resolve to the new bucket,
    // then re-read each saved library for the now-active persona (admin and rep
    // keep separate "My …" reports/dashboards/goals).
    savePersona(next)
    setPersona(next)
    seedStarterKpi()
    setSavedViews(listSavedViews())
    setSavedDashboards(listSavedDashboards())
    setKpis(listKpis())
  }

  // The persona only curates which dashboards/reports show (and, for the rep,
  // scopes them to CURRENT_REP) — the data and engine are identical for both.
  const dashboards = DASHBOARDS.filter((d) =>
    visibleForPersona(d.personas, persona)
  )
  const templates = TEMPLATES.filter((t) =>
    visibleForPersona(t.personas, persona)
  )

  const handleDelete = (id: string) => {
    deleteSavedView(id)
    setSavedViews(listSavedViews())
  }

  const handleDeleteDashboard = (id: string) => {
    deleteSavedDashboard(id)
    setSavedDashboards(listSavedDashboards())
  }

  const handleDeleteKpi = (id: string) => {
    deleteKpi(id)
    setKpis(listKpis())
  }

  const handleSaveKpi = (
    id: string,
    patch: { name: string; value: number }
  ) => {
    updateKpi(id, patch)
    setKpis(listKpis())
  }

  // Duplicate clones the source into the user's own copies (shipped or saved
  // alike) so it can be tweaked without touching the original.
  const handleDuplicateView = (view: ViewConfig) => {
    saveView(view, `${view.name} (copy)`)
    setSavedViews(listSavedViews())
  }

  const handleDuplicateKpi = (kpi: Kpi) => {
    saveKpi({
      name: `${kpi.name} (copy)`,
      value: kpi.value,
      unit: kpi.unit,
      dir: kpi.dir,
    })
    setKpis(listKpis())
  }

  const handleDuplicateDashboard = (d: Dashboard) => {
    saveDashboard(`${d.name} (copy)`, d.tiles, d.description)
    setSavedDashboards(listSavedDashboards())
  }

  const handleAddToDashboard = (view: ViewConfig, dashboardId: string) => {
    const updated = addReportToDashboard(dashboardId, view)
    setSavedDashboards(listSavedDashboards())
    // Success cue — the popover just closes otherwise, with no sign it worked.
    toast.success(`Added "${view.name}" to ${updated?.name ?? 'dashboard'}`)
  }

  // Open the name dialog rather than creating "New dashboard" outright (see the
  // SaveDialog below) so the user names it before it's saved.
  const handleAddToNewDashboard = (view: ViewConfig) => setNewDashView(view)

  const handleCreateNewDashboard = (name: string, description: string) => {
    if (!newDashView) return
    const created = createDashboardFromReport(
      name,
      newDashView,
      description || undefined
    )
    setSavedDashboards(listSavedDashboards())
    toast.success(`Added "${newDashView.name}" to ${created.name}`)
  }

  // One library table over every report: shipped templates (Pre-built) + the
  // user's saved views and goals (Mine). LibraryTable owns the source/type/search
  // filtering (filterable), so the dashboard builder's picker filters identically.
  // "Add to dashboard" targets only the user's own (editable) dashboards.
  const dashboardTargets = savedDashboards.map((d) => ({
    id: d.id,
    name: d.name,
  }))
  const addToDashboardProps = (view: ViewConfig) => ({
    dashboards: dashboardTargets,
    onAddToDashboard: (dashboardId: string) =>
      handleAddToDashboard(view, dashboardId),
    onAddToNewDashboard: () => handleAddToNewDashboard(view),
  })

  const allRows: LibraryRow[] = [
    ...templates.map((t) => ({
      kind: 'view' as const,
      source: 'prebuilt' as const,
      view: t,
      onDuplicate: () => handleDuplicateView(t),
      ...addToDashboardProps(t),
    })),
    ...savedViews.map((v) => ({
      kind: 'view' as const,
      source: 'mine' as const,
      view: v,
      onDuplicate: () => handleDuplicateView(v),
      onDelete: () => handleDelete(v.id),
      ...addToDashboardProps(v),
    })),
    ...kpis.map((k) => ({
      kind: 'goal' as const,
      source: 'mine' as const,
      kpi: k,
      onSave: (patch: { name: string; value: number }) =>
        handleSaveKpi(k.id, patch),
      onDelete: () => handleDeleteKpi(k.id),
      onDuplicate: () => handleDuplicateKpi(k),
    })),
  ]

  return (
    <div className={cn(REPORTING_PAGE_SHELL, 'gap-8')}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-fg">Advanced Reporting</h1>
          <p className="text-sm text-fg-secondary">
            {persona === 'rep'
              ? `Viewing as ${CURRENT_REP} — your deals and quota.`
              : 'Prototype — sample data, not connected to your CRM.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-fg-secondary">Viewing as</span>
            <PillTabs
              className="w-fit"
              idPrefix="reporting-persona"
              aria-label="Viewing as"
              tabs={PERSONAS.map((p) => ({ key: p.id, title: p.label }))}
              activeKey={persona}
              onChange={(key) => changePersona(key as Persona)}
            />
          </div>
          <Link to="/reporting/new" className={primaryPillButtonClass}>
            <PiPlus size={16} /> Build your own
          </Link>
        </div>
      </div>

      {/* Dashboards stay split (My / Pre-built); reports below merge into one
          source-filtered table. */}
      <section>
        <h2 className="text-sm font-semibold text-fg-secondary uppercase tracking-wide mb-3">
          My dashboards
        </h2>
        {savedDashboards.length === 0 ? (
          <p className="text-sm text-fg-secondary">
            Nothing yet — duplicate a dashboard or use &ldquo;Build your
            own&rdquo;.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {savedDashboards.map((d) => (
              <DashboardCard
                key={d.id}
                dashboard={d}
                onDuplicate={() => handleDuplicateDashboard(d)}
                onDelete={() => handleDeleteDashboard(d.id)}
              />
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-sm font-semibold text-fg-secondary uppercase tracking-wide mb-3">
          Pre-built dashboards
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {dashboards.map((d) => (
            <DashboardCard
              key={d.id}
              dashboard={d}
              onDuplicate={() => handleDuplicateDashboard(d)}
            />
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-fg-secondary uppercase tracking-wide mb-3">
          Reports
        </h2>
        {/* Goals list here too — reusable target values, not reports. */}
        <LibraryTable rows={allRows} filterable />
      </section>

      <SaveDialog
        open={newDashView !== null}
        onClose={() => setNewDashView(null)}
        kind="dashboard"
        titleOverride="New dashboard"
        defaultName={newDashView?.name ?? ''}
        onSaveNew={({ name, description }) =>
          handleCreateNewDashboard(name, description)
        }
      />
    </div>
  )
}
